import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Avatar,
  Switch,
  Animate,
  EmptyState,
  useToast
} from 'wss3-forge'
import {
  Apps20Regular,
  Eye20Regular
} from '@fluentui/react-icons'

interface ConnectedApp {
  id: string
  app_id: string
  app_name: string
  app_image: string | null
  first_login_at: string
  last_login_at: string
  login_count: number
}

interface PrivacySettings {
  activity_tracking: boolean
}

export function Privacy() {
  const { user } = useAuth()
  const toast = useToast()
  usePageTitle('Privacy')
  const [connectedApps, setConnectedApps] = useState<ConnectedApp[]>([])
  const [settings, setSettings] = useState<PrivacySettings>({
    activity_tracking: true
  })
  const [revoking, setRevoking] = useState<string | null>(null)
  const initialLoadDone = useRef(false)

  useEffect(() => {
    if (user && !initialLoadDone.current) {
      loadData()
    }
  }, [user])

  const loadData = async () => {
    if (!user) return

    try {
      // Query app_users joined with applications to get connected apps
      const { data, error } = await supabase
        .from('app_users')
        .select(`
          id,
          app_id,
          first_login_at,
          last_login_at,
          login_count,
          applications (
            name,
            image_url
          )
        `)
        .eq('user_id', user.id)
        .order('last_login_at', { ascending: false })

      if (error) throw error

      const apps: ConnectedApp[] = (data || []).map((item: any) => ({
        id: item.id,
        app_id: item.app_id,
        app_name: item.applications?.name || 'Unknown App',
        app_image: item.applications?.image_url || null,
        first_login_at: item.first_login_at,
        last_login_at: item.last_login_at,
        login_count: item.login_count
      }))

      setConnectedApps(apps)
    } catch (err) {
      console.error('Failed to load connected apps:', err)
    }

    initialLoadDone.current = true
  }

  const handleRevokeAccess = async (appUserId: string) => {
    setRevoking(appUserId)
    try {
      const { error } = await supabase
        .from('app_users')
        .delete()
        .eq('id', appUserId)

      if (error) throw error

      setConnectedApps(prev => prev.filter(app => app.id !== appUserId))
      toast.success('Access revoked successfully')
    } catch (err) {
      console.error('Error revoking access:', err)
      toast.error('Failed to revoke access')
    } finally {
      setRevoking(null)
    }
  }

  const handleSettingChange = (key: keyof PrivacySettings, value: boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }))
    toast.success('Privacy setting updated')
  }

  return (
    <>
    <div className="page-content">
      <VStack gap="md">
        <Animate type="fadeIn">
          <VStack gap="xs">
            <Heading level={2}>Privacy Settings</Heading>
            <Text color="muted">Manage your privacy and connected applications</Text>
          </VStack>
        </Animate>

        <Animate type="slideInUp" delay={50}>
          <Card padding="md">
            <VStack gap="sm">
              <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                <Apps20Regular style={{ color: 'var(--brand-primary)' }} />
                <Heading level={5}>Connected Applications</Heading>
              </HStack>

              {connectedApps.length === 0 ? (
                <EmptyState
                  icon={<Apps20Regular />}
                  title="No connected applications"
                  description="Applications you sign into with Webba ID will appear here"
                />
              ) : (
                <VStack gap="md">
                  {connectedApps.map((app) => (
                    <HStack
                      key={app.id}
                      gap="md"
                      style={{
                        alignItems: 'center',
                        padding: '0.75rem',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: 'var(--bg-tertiary)'
                      }}
                    >
                      <Avatar name={app.app_name} src={app.app_image || undefined} size="md" />
                      <VStack gap="none" style={{ flex: 1 }}>
                        <Text weight="medium">{app.app_name}</Text>
                        <Text size="sm" color="muted">
                          Last login {new Date(app.last_login_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })} · {app.login_count} login{app.login_count > 1 ? 's' : ''}
                        </Text>
                      </VStack>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeAccess(app.id)}
                        loading={revoking === app.id}
                        disabled={revoking === app.id}
                      >
                        Revoke
                      </Button>
                    </HStack>
                  ))}
                </VStack>
              )}
            </VStack>
          </Card>
        </Animate>

        <Animate type="slideInUp" delay={100}>
          <Card padding="md">
            <VStack gap="sm">
              <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
                <Eye20Regular style={{ color: 'var(--brand-primary)' }} />
                <Heading level={5}>Data Sharing</Heading>
              </HStack>

              <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <VStack gap="xs">
                  <Text weight="medium">Activity tracking</Text>
                  <Text size="sm" color="muted">
                    Allow Webba to track usage for improving services
                  </Text>
                </VStack>
                <Switch
                  checked={settings.activity_tracking}
                  onChange={(checked) => handleSettingChange('activity_tracking', checked)}
                />
              </HStack>
            </VStack>
          </Card>
        </Animate>
      </VStack>
    </div>
    </>
  )
}
